import { AdminResellerStatsData, ResellerType } from '../AdminResellerStats';
import { formatSalesPeriod } from './dateUtils';

interface BookletStatsItem {
  displayName: string;
  sold: number;
  supportSold: number;
  earnings: number;
  afEarnings: number;
  supportEarnings: number;
}

interface AssociationItem extends BookletStatsItem {
  association: {
    _id: string;
    adminEmails?: string[];
    phone?: string;
    bankInformation?: {
      clearingNumber?: string;
      accountNumber?: string;
      accountOwner?: string;
    };
    salesStartDate?: string | null;
    salesEndDate?: string | null;
    paidOutByBooklet?: Array<{ bookletId: string; amount: number }>;
  };
}

interface TeamItem extends BookletStatsItem {
  team: {
    _id: string;
    adminEmails?: string[];
    bankInformation?: {
      clearingNumber?: string;
      accountNumber?: string;
      accountOwner?: string;
    };
    salesStartDate?: string | null;
    salesEndDate?: string | null;
    paidOutByBooklet?: Array<{ bookletId: string; amount: number }>;
  };
}

interface TeamMemberItem extends BookletStatsItem {
  teamMember: {
    _id: string;
    adminEmails?: string[];
  };
}

export const mapAssociation = (
  item: AssociationItem,
  bookletId: string,
  totalYourEarnings: number,
  isMain: boolean = false
): AdminResellerStatsData => {
  const paidOutEntry = item.association.paidOutByBooklet?.find(p => p.bookletId === bookletId);
  return {
    resellerId: item.association._id,
    resellerType: ResellerType.Association,
    name: item.displayName,
    sold: item.sold,
    supportSold: item.supportSold || 0,
    afEarnings: item.afEarnings,
    yourEarnings: item.earnings,
    earningsToCompare: isMain ? totalYourEarnings : item.afEarnings,
    supportEarnings: item.supportEarnings || 0,
    email: item.association.adminEmails?.join(', '),
    phone: item.association.phone,
    clearingNumber: item.association.bankInformation?.clearingNumber,
    accountNumber: item.association.bankInformation?.accountNumber,
    accountOwner: item.association.bankInformation?.accountOwner,
    salesPeriod: formatSalesPeriod(item.association.salesStartDate, item.association.salesEndDate),
    salesStartDate: item.association.salesStartDate,
    salesEndDate: item.association.salesEndDate,
    paidOut: paidOutEntry?.amount || 0,
    isMainAssociation: isMain
  };
};

export const mapTeam = (
  item: TeamItem,
  bookletId: string
): AdminResellerStatsData => {
  const paidOutEntry = item.team.paidOutByBooklet?.find(p => p.bookletId === bookletId);
  return {
    resellerId: item.team._id,
    resellerType: ResellerType.Team,
    name: item.displayName,
    sold: item.sold,
    supportSold: 0, // Lag får inga stödandelar
    afEarnings: item.afEarnings,
    yourEarnings: item.earnings,
    earningsToCompare: item.afEarnings,
    supportEarnings: item.supportEarnings || 0,
    email: item.team.adminEmails?.join(', '),
    phone: undefined,
    clearingNumber: item.team.bankInformation?.clearingNumber,
    accountNumber: item.team.bankInformation?.accountNumber,
    accountOwner: item.team.bankInformation?.accountOwner,
    salesPeriod: formatSalesPeriod(item.team.salesStartDate, item.team.salesEndDate),
    salesStartDate: item.team.salesStartDate,
    salesEndDate: item.team.salesEndDate,
    paidOut: paidOutEntry?.amount || 0
  };
};

export const mapTeamMember = (
  item: TeamMemberItem
): AdminResellerStatsData => {
  return {
    resellerId: item.teamMember._id,
    resellerType: ResellerType.TeamMember,
    name: item.displayName,
    sold: item.sold,
    supportSold: 0, // Lagmedlemmar får inga stödandelar
    afEarnings: item.afEarnings,
    yourEarnings: item.earnings,
    earningsToCompare: item.afEarnings,
    supportEarnings: item.supportEarnings || 0,
    email: item.teamMember.adminEmails?.join(', '),
    phone: undefined,
    clearingNumber: undefined,
    accountNumber: undefined,
    accountOwner: undefined,
    salesPeriod: undefined,
    salesStartDate: undefined,
    salesEndDate: undefined,
    paidOut: 0
  };
};

